import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CreditCard, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface TokenPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  savings_percentage: number;
  is_popular: boolean;
  is_best_value: boolean;
}

interface TokenPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchase: (packageId: string, amount: number) => void;
}

const TokenPurchaseModal = ({ isOpen, onClose, onPurchase }: TokenPurchaseModalProps) => {
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalWithFee = (basePrice: number) => {
    const transactionFee = Math.round(basePrice * 0.175); // 17.5% total fee (10% processing + 7.5% platform)
    return basePrice + transactionFee;
  };

  const handlePurchase = (packageId: string, price: number) => {
    const totalWithFee = calculateTotalWithFee(price);
    onPurchase(packageId, totalWithFee);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center text-energy">Choose Your Token Package</DialogTitle>
          <div className="mt-2 flex flex-col items-center gap-2">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-900/50 to-green-700/50 rounded-full border border-green-500/30">
              <span className="text-green-300 font-medium text-sm">✓ Buy Now, Pay Later options available with Klarna, Affirm & Afterpay</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Includes 10% tax and processing fee + 7.5% platform fee
            </div>
          </div>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {packages.map((pkg) => {
              const savings = pkg.savings_percentage;
              const savingsAmount = Math.round(pkg.price_cents * (savings / 100));

              return (
                <Card 
                  key={pkg.id} 
                  className={`relative obsidian-card ${
                    pkg.is_popular ? 'border-primary shadow-lg energy-glow' : 
                    pkg.is_best_value ? 'border-2 border-gold/50 shadow-xl' : ''
                  }`}
                >
                  {pkg.is_popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium energy-glow">
                        Most Popular
                      </span>
                    </div>
                  )}
                  {pkg.is_best_value && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-yellow-600 to-yellow-400 text-black px-4 py-1 rounded-full text-sm font-bold">
                        Best Value
                      </span>
                    </div>
                  )}
                  
                  <CardHeader className="text-center">
                    <CardTitle className="text-xl text-energy">
                      {pkg.credits} Token{pkg.credits > 1 ? 's' : ''}
                    </CardTitle>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-primary">
                        ${(pkg.price_cents / 100).toFixed(0)}
                      </div>
                    </div>
                    {savings > 0 && (
                      <p className="text-sm text-green-500 font-medium">
                        Save {savings}% (${(savingsAmount / 100).toFixed(0)})
                      </p>
                    )}
                  </CardHeader>
                  
                  <CardContent className="text-center">
                    <Button 
                      className="w-full energy-button" 
                      onClick={() => handlePurchase(pkg.id, pkg.price_cents)}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        
        <div className="mt-6 text-center">
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full border border-primary/30">
            <span className="text-energy font-semibold text-sm">
              💡 Tokens valid for 1 year • No subscriptions • Flexible scheduling
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TokenPurchaseModal;
